import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Briefcase } from 'lucide-react';

const initialFormData = {
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  roleCategory: '',
  organisation: '',
  department: '',
  type: 'Internal',
  primary: false,
  email: '',
  phone: '',
  notes: ''
};

export default function AddAdviserModal({ isOpen, onClose, onSave, clientId, initialData, isEdit }) {
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && initialData) {
      // Handle both old format (name) and new format (firstName/lastName)
      let firstName = initialData.firstName || '';
      let lastName = initialData.lastName || '';
      if (!firstName && !lastName && initialData.name) {
        // Parse old format name into firstName/lastName
        const nameParts = initialData.name.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }
      
      setFormData({
        firstName: firstName,
        middleName: initialData.middleName || '',
        lastName: lastName,
        preferredName: initialData.preferredName || '',
        roleCategory: initialData.roleCategory || '',
        organisation: initialData.organisation || '',
        department: initialData.department || '',
        type: initialData.type || 'Internal',
        primary: initialData.primary || false,
        email: initialData.email || '',
        phone: initialData.phone || '',
        notes: initialData.notes || ''
      });
    } else {
      setFormData(initialFormData);
    }
  }, [isEdit, initialData, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.roleCategory) newErrors.roleCategory = 'Role category is required';
    if (!formData.organisation.trim()) newErrors.organisation = 'Organisation is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const adviserData = {
        ...formData,
        // Also include combined name for backward compatibility
        name: `${formData.firstName}${formData.middleName ? ' ' + formData.middleName : ''} ${formData.lastName}`.trim(),
        clientId: clientId,
        lastVerified: new Date().toISOString().split('T')[0],
        worksWithIndividuals: [],
        worksWithEntities: []
      };
      
      await onSave(adviserData);
      setFormData(initialFormData);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to save adviser:', error);
      setErrors({ submit: 'Failed to save adviser. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] bg-white max-h-[90vh] overflow-y-auto" data-testid="add-adviser-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#2C2420]">
            <Briefcase className="w-5 h-5 text-[#4A3B32]" />
            {isEdit ? 'Edit Adviser' : 'Add New Adviser'}
          </DialogTitle>
          <DialogDescription className="text-[#5D524D]">
            {isEdit ? 'Update the details for this adviser.' : 'Add a new adviser to this client group.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-[#2C2420]">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="e.g., John"
                className={`caffeine-input ${errors.firstName ? 'border-red-500' : ''}`}
                data-testid="adviser-firstname-input"
              />
              {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
            </div>

            {/* Middle Name */}
            <div className="space-y-2">
              <Label htmlFor="middleName" className="text-[#2C2420]">Middle Name</Label>
              <Input
                id="middleName"
                value={formData.middleName}
                onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                placeholder="e.g., William"
                className="caffeine-input"
                data-testid="adviser-middlename-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-[#2C2420]">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="e.g., Smith"
                className={`caffeine-input ${errors.lastName ? 'border-red-500' : ''}`}
                data-testid="adviser-lastname-input"
              />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
            </div>

            {/* Preferred Name */}
            <div className="space-y-2">
              <Label htmlFor="preferredName" className="text-[#2C2420]">Preferred Name</Label>
              <Input
                id="preferredName"
                value={formData.preferredName}
                onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
                placeholder="e.g., Jack"
                className="caffeine-input"
                data-testid="adviser-preferredname-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Role Category */}
            <div className="space-y-2">
              <Label className="text-[#2C2420]">
                Role Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.roleCategory}
                onValueChange={(value) => setFormData({ ...formData, roleCategory: value })}
              >
                <SelectTrigger className={`caffeine-input ${errors.roleCategory ? 'border-red-500' : ''}`} data-testid="adviser-role-select">
                  <SelectValue placeholder="Select role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="Partner">Partner</SelectItem>
                  <SelectItem value="Wealth">Wealth</SelectItem>
                  <SelectItem value="Tax">Tax</SelectItem>
                  <SelectItem value="Lawyer">Lawyer</SelectItem>
                  <SelectItem value="Accountant">Accountant</SelectItem>
                  <SelectItem value="Insurance">Insurance</SelectItem>
                  <SelectItem value="Banking">Banking</SelectItem>
                </SelectContent>
              </Select>
              {errors.roleCategory && <p className="text-sm text-red-500">{errors.roleCategory}</p>}
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label className="text-[#2C2420]">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger className="caffeine-input" data-testid="adviser-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Internal">Internal</SelectItem>
                  <SelectItem value="External">External</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Organisation */}
          <div className="space-y-2">
            <Label htmlFor="organisation" className="text-[#2C2420]">
              Organisation <span className="text-red-500">*</span>
            </Label>
            <Input
              id="organisation"
              value={formData.organisation}
              onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
              placeholder="e.g., QIndex One or BDO"
              className={`caffeine-input ${errors.organisation ? 'border-red-500' : ''}`}
              data-testid="adviser-organisation-input"
            />
            {errors.organisation && <p className="text-sm text-red-500">{errors.organisation}</p>}
          </div>

          {/* Department */}
          <div className="space-y-2">
            <Label htmlFor="department" className="text-[#2C2420]">Department</Label>
            <Input
              id="department"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              placeholder="e.g., Client Services"
              className="caffeine-input"
              data-testid="adviser-department-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2C2420]">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="e.g., john@example.com"
                className="caffeine-input"
                data-testid="adviser-email-input"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#2C2420]">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g., +61 2 9999 1234"
                className="caffeine-input"
                data-testid="adviser-phone-input"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-[#2C2420]">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="e.g., Relationship Lead, specialises in..."
              className="caffeine-input min-h-[80px]"
              data-testid="adviser-notes-input"
            />
          </div>

          {/* Primary Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-[#2C2420]">Primary Adviser</Label>
              <p className="text-xs text-[#8C8078]">Mark as primary contact for this category</p>
            </div>
            <Switch
              checked={formData.primary}
              onCheckedChange={(checked) => setFormData({ ...formData, primary: checked })}
              data-testid="adviser-primary-toggle"
            />
          </div>

          {errors.submit && <p className="text-sm text-red-500 text-center">{errors.submit}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="caffeine-btn-ghost" data-testid="cancel-add-adviser-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="caffeine-btn" data-testid="save-adviser-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (isEdit ? 'Save Changes' : 'Add Adviser')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
